/**
 * assets/pod-order-api.js
 * POD 订单 API 封装 - 处理所有与订单相关的 Shopify 数据写入
 * 对接 Cloudflare Worker: pod-order-worker.js
 */

const PODOrderAPI = {
  // 配置（从 section settings 或 window.POD_CONFIG 获取）
  config: {
    workerEndpoint: '',
    maxFileMB: 10,
    hmacSecret: ''  // 关键修复 (DSN-1): 从 POD_CONFIG 读取, 用于调 internal-api 时签名
  },

  /**
   * 初始化配置
   * @param {Object} options - 配置选项
   */
  init(options = {}) {
    this.config = { ...this.config, ...options };

    // 尝试从 window.POD_CONFIG 获取配置
    if (typeof window !== 'undefined' && window.POD_CONFIG) {
      const firstConfig = Object.values(window.POD_CONFIG)[0];
      if (firstConfig) {
        // 设计师 Dashboard 用 workerEndpoint（pod-internal-api）
        this.config.workerEndpoint = this.config.workerEndpoint || firstConfig.workerEndpoint || '';
        this.config.maxFileMB = this.config.maxFileMB || firstConfig.maxFileMB || 10;
        this.config.hmacSecret = this.config.hmacSecret || firstConfig.hmacSecret || '';
      }
    }

    // fallback 到全局 window.POD_WORKER_ENDPOINT（pod-internal-api）
    if (!this.config.workerEndpoint && typeof window !== 'undefined' && window.POD_WORKER_ENDPOINT) {
      this.config.workerEndpoint = window.POD_WORKER_ENDPOINT;
    }
  },

  /**
   * 检查是否配置了 Worker
   */
  isConfigured() {
    return !!this.config.workerEndpoint;
  },

  // ==================== R2 上传 ====================

  /**
   * 上传文件到 R2（multipart，免费 plan 兼容）
   * Send Later 场景: 传 orderId → Worker 生成 uploads/ORD-{orderId}/{filename} 路径
   * 与 Wizard (podw-upload-worker.js) 完全一致的 cdnUrl 格式
   * @param {File} file - 文件对象
   * @param {Object} options - 上传选项
   * @param {string} options.orderId - 订单 ID（有则直接写入 uploads/ORD-{orderId}/）
   * @param {string} options.folder - 目标文件夹（仅 orderId 为空时使用）
   * @returns {Promise<{publicUrl: string, cdnUrl: string, key: string}>}
   */
  async uploadFileToR2(file, options = {}) {
    const orderId = options.orderId || '';
    const folder = options.folder || 'proofs';

    if (!this.isConfigured()) {
      console.warn('[PODOrderAPI] Worker not configured, using mock mode');
      return this.mockGetSignedUrl(file);
    }

    if (!this.config.hmacSecret) {
      console.warn('[PODOrderAPI] hmacSecret missing — r2-sign will likely 401');
    }

    try {
      const formData = new FormData();
      formData.append('file', file);
      if (orderId) {
        formData.append('orderId', orderId);
      } else {
        formData.append('folder', folder);
      }
      // 设计师上传校稿时加 proofs/ 子文件夹标记
      if (options.proof) {
        formData.append('proof', '1');
      }

      const ts = Math.floor(Date.now() / 1000).toString();
      const sig = await window.POD_HMAC.signHex(this.config.hmacSecret || '', ':' + ts);

      const response = await fetch(`${this.config.workerEndpoint}/r2-signer`, {
        method: 'POST',
        headers: {
          'X-Pod-Signature': sig,
          'X-Pod-Timestamp': ts
        },
        body: formData
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || `Upload failed: ${response.status}`);
      }

      const result = await response.json();
      // 统一返回 publicUrl 和 cdnUrl（两者相同，与 Wizard 保持一致）
      return {
        publicUrl: result.publicUrl || result.cdnUrl || '',
        cdnUrl: result.cdnUrl || result.publicUrl || '',
        key: result.key || ''
      };
    } catch (error) {
      console.error('[PODOrderAPI] Error uploading to R2:', error);
      throw error;
    }
  },

  /**
   * 获取 R2 签名 URL（已废弃，保留签名避免调用方报错）
   * 旧逻辑走 presigned PUT，免费 plan 不支持 createPresignedUrl
   * 新逻辑直接 multipart 上传，见 uploadFileToR2
   * @deprecated
   */
  async getR2SignedUrl(file, folder = 'proofs') {
    console.warn('[PODOrderAPI] getR2SignedUrl is deprecated, use uploadFileToR2');
    return this.uploadFileToR2(file, folder);
  },

  /**
   * 上传文件到 R2（PUT 到签名 URL）—— 旧 presigned 模式，免费 plan 不可用
   * @deprecated
   */
  async uploadToR2(signedUrl, file, onProgress) {
    console.warn('[PODOrderAPI] uploadToR2 is deprecated');
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable && onProgress) {
          onProgress(Math.round((e.loaded / e.total) * 100));
        }
      });
      xhr.addEventListener('load', () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve();
        } else {
          reject(new Error(`Upload failed: ${xhr.status}`));
        }
      });
      xhr.addEventListener('error', () => reject(new Error('Network error')));
      xhr.addEventListener('abort', () => reject(new Error('Upload aborted')));
      xhr.open('PUT', signedUrl);
      xhr.setRequestHeader('Content-Type', file.type);
      xhr.send(file);
    });
  },

  /**
   * 完整的上传流程（multipart，免费 plan 可用）
   * @param {File} file - 文件
   * @param {Object} options - 上传选项 {orderId, folder}
   * @param {Function} onProgress - 进度回调（multipart 无法精确进度，按 50/100 模拟）
   * @returns {Promise<{publicUrl, cdnUrl, key}>}
   */
  async uploadFile(file, options = {}, onProgress) {
    if (typeof options === 'string') {
      // 兼容旧调用方式：uploadFile(file, 'folder')
      options = { folder: options };
    }
    if (onProgress) onProgress(50);
    const result = await this.uploadFileToR2(file, options);
    if (onProgress) onProgress(100);
    return { publicUrl: result.publicUrl, cdnUrl: result.cdnUrl, key: result.key };
  },

  // ==================== 订单操作 ====================

  /**
   * 设计师发送校稿
   * @param {string} orderId - 订单 ID
   * @param {string} proofImageUrl - 校稿图片公共 URL
   * @param {string} notes - 备注
   * @returns {Promise<Object>}
   */
  async sendProof(orderId, proofImageUrl, notes = '', idempotencyKey = '') {
    return this.callWorker('/order/send-proof', {
      orderId,
      proofImageUrl,
      notes,
      idempotencyKey
    });
  },

  /**
   * 客户批准校稿
   * @param {string} orderId - 订单 ID
   * @param {string} feedback - 反馈（可选）
   * @returns {Promise<Object>}
   */
  async approveProof(orderId, feedback = '') {
    /* P1-7: 把页面 URL 中的 token/expires 一起传给 Worker (与 GET /order/proof 共享鉴权)
     * 此端点用页面 token 鉴权, 不走 HMAC (callWorkerUnsigned). */
    const section = document.querySelector('[data-section-type="pod-proof-viewer-page"]');
    const token = (section && section.dataset.proofToken) || '';
    const expires = (section && section.dataset.proofExpires) || '';
    return this.callWorkerUnsigned('/order/approve', {
      orderId,
      feedback,
      token,
      expires
    });
  },

  /**
   * 客户拒绝校稿
   * @param {string} orderId - 订单 ID
   * @param {string} feedback - 修改要求（必填）
   * @returns {Promise<Object>}
   */
  async rejectProof(orderId, feedback) {
    if (!feedback) {
      throw new Error('Feedback is required when rejecting a proof');
    }
    /* P1-7: 与 approve 共享 token 鉴权, 不走 HMAC. */
    const section = document.querySelector('[data-section-type="pod-proof-viewer-page"]');
    const token = (section && section.dataset.proofToken) || '';
    const expires = (section && section.dataset.proofExpires) || '';
    return this.callWorkerUnsigned('/order/reject', {
      orderId,
      feedback,
      token,
      expires
    });
  },

  /**
   * 通用订单更新
   * @param {string} orderId - 订单 ID
   * @param {Object} data - 要更新的数据
   * @returns {Promise<Object>}
   */
  async updateOrder(orderId, data) {
    return this.callWorker('/order/update', {
      orderId,
      data
    });
  },

  /**
   * 客户上传照片（Send Later 场景）
   * 使用 orderId → 上传到 uploads/ORD-{orderId}/（与 Wizard 一致的路径格式）
   * @param {string} orderId - 订单 ID
   * @param {File[]} images - 图片文件数组
   * @param {Function} onProgress - 进度回调
   * @returns {Promise<Object>} { uploadedUrls }
   */
  async uploadCustomerPhotos(orderId, images, onProgress) {
    const uploadedUrls = [];
    for (let i = 0; i < images.length; i++) {
      const { publicUrl } = await this.uploadFile(images[i], { orderId }, (pct) => {
        if (onProgress) {
          onProgress(Math.round(((i + pct / 100) / images.length) * 100));
        }
      });
      uploadedUrls.push(publicUrl);
    }
    return { uploadedUrls };
  },

  /**
   * P2-6: 合并 uploadCustomerPhotos + submitCustomerPhotos
   * 调用方应使用此方法：先上传到 R2，再一次性写入 Shopify
   * @param {string} orderId - 订单 ID
   * @param {File[]} images - 图片文件数组
   * @param {Function} onProgress - 进度回调
   */
  async uploadAndSubmitPhotos(orderId, images, onProgress) {
    // Step 1: 上传到 R2
    const { uploadedUrls } = await this.uploadCustomerPhotos(orderId, images, onProgress);
    // Step 2: 一次性写入 Shopify（包含 Image 1/2/3 + _upload_mode）
    return this.submitCustomerPhotos(orderId, uploadedUrls);
  },

  /**
   * P2-6: 一次性写入 Shopify：Image URLs + _upload_mode=Submitted
   * @param {string} orderId - 订单 ID
   * @param {string[]} uploadedUrls - 已上传到 R2 的 URL 列表
   * @param {Object} options - 可选参数，包含 token 和 sessionId 用于鉴权
   */
  async submitCustomerPhotos(orderId, uploadedUrls, options = {}) {
    if (!orderId) throw new Error('orderId is required');
    if (!Array.isArray(uploadedUrls) || uploadedUrls.length === 0) {
      throw new Error('uploadedUrls must be a non-empty array');
    }
    return this.callWorker('/order/customer-photos', {
      orderId,
      uploadedUrls,
      submittedAt: new Date().toISOString(),
      token: options.token || '',
      sessionId: options.sessionId || ''
    });
  },

  /**
   * 20260723-FIX: 仅保存图片 URL（不标记 _upload_mode=Submitted）
   * 用于 Auto-save，让刷新页面后能恢复图片，但不会触发 Shopify Flow
   * @param {string} orderId - 订单 ID
   * @param {string[]} uploadedUrls - 已上传到 R2 的 URL 列表
   * @param {Object} options - 可选参数，包含 token 和 sessionId 用于鉴权
   */
  async saveCustomerPhotos(orderId, uploadedUrls, options = {}) {
    if (!orderId) throw new Error('orderId is required');
    // 20260723-FIX: 允许空数组（删除最后一张图片时需要清空）
    if (!Array.isArray(uploadedUrls)) {
      uploadedUrls = [];
    }
    // 空数组时仍然调用 API（让 Worker 清空图片）
    return this.callWorker('/order/customer-photos', {
      orderId,
      uploadedUrls,
      _saveOnly: true,  // 标记为仅保存，不标记完成
      token: options.token || '',
      sessionId: options.sessionId || ''
    });
  },

  // ==================== 订单状态中心 ====================

  /**
   * 获取订单 POD 状态（供订单状态中心页面）
   * @param {string} orderId - 订单 ID
   * @param {string} sessionId - Session ID (用于 HMAC 验证)
   * @param {string} token - HMAC token
   * @returns {Promise<Object>} 订单状态信息
   */
  async getOrderStatus(orderId, sessionId, token) {
    if (!orderId) throw new Error('orderId is required');
    if (!sessionId) throw new Error('sessionId is required');
    if (!token) throw new Error('token is required');

    const endpoint = `${this.config.workerEndpoint}/api/pod/order-status`;
    const url = `${endpoint}?orderId=${encodeURIComponent(orderId)}&sessionId=${encodeURIComponent(sessionId)}&token=${encodeURIComponent(token)}`;

    try {
      const response = await fetch(url, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' }
      });

      if (!response.ok) {
        let err = {};
        try { err = await response.json(); } catch (e) {}
        throw new Error(err.error || `Fetch failed: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('[PODOrderAPI] Error getting order status:', error);
      throw error;
    }
  },

  // ==================== 图片删除/查询 ====================

  /**
   * 删除订单某张客户图（R2 + Shopify note_attributes 一并处理）
   * DSN-1 修复: 走签名 DELETE, HMAC 头加在请求里
   * @param {string} orderId - 订单 ID
   * @param {string} imageKey - 形如 "Image 1" / "Image 2"
   * @returns {Promise<Object>}
   */
  async deleteOrderImage(orderId, imageKey) {
    if (!orderId) throw new Error('orderId is required');
    if (!imageKey) throw new Error('imageKey is required (e.g. "Image 1")');

    if (!this.isConfigured()) {
      console.warn('[PODOrderAPI] Worker not configured, using mock for delete');
      return this.mockCall('/order/image', { orderId, imageKey, _delete: true });
    }

    if (!this.config.hmacSecret) {
      console.warn('[PODOrderAPI] hmacSecret missing — deleteOrderImage will likely 401');
    }

    const ts = Math.floor(Date.now() / 1000).toString();
    const sig = await window.POD_HMAC.signHex(this.config.hmacSecret || '', orderId + ':' + ts);

    const response = await fetch(`${this.config.workerEndpoint}/order/image`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        'X-Pod-Signature': sig,
        'X-Pod-Timestamp': ts
      },
      body: JSON.stringify({ orderId, imageKey })
    });

    if (!response.ok) {
      let err = {};
      try { err = await response.json(); } catch (e) {}
      throw new Error(err.error || `Delete failed: ${response.status}`);
    }

    return response.json();
  },

  /**
   * 删除 R2 文件（用于设计师撤销上传的 proof 图片）
   * @param {string} key - R2 文件的 key（如 uploads/2026-07/ORD-123/file.jpg）
   */
  async deleteFile(key) {
    if (!key) throw new Error('key is required');

    if (!this.isConfigured()) {
      console.warn('[PODOrderAPI] Worker not configured, skipping delete');
      return { success: true, mock: true };
    }

    try {
      const response = await fetch(`${this.config.workerEndpoint}/upload?key=${encodeURIComponent(key)}`, {
        method: 'DELETE'
      });

      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.error || `Delete failed: ${response.status}`);
      }

      console.log('[PODOrderAPI] Deleted R2 file:', key);
      return await response.json();
    } catch (e) {
      console.error('[PODOrderAPI] deleteFile failed:', e.message);
      throw e;
    }
  },

  /**
   * 查询订单所有客户图（Shopify metafields.Image 1..5）
   * DSN-1 修复: 加 X-Pod-Signature + X-Pod-Timestamp
   * @param {string} orderId - 订单 ID
   * @returns {Promise<{orderId:string, images:Array, count:number}>}
   */
  async getOrderImages(orderId) {
    if (!orderId) throw new Error('orderId is required');

    if (!this.isConfigured()) {
      console.warn('[PODOrderAPI] Worker not configured, mock getOrderImages');
      return { orderId, images: [], count: 0, mock: true };
    }

    if (!this.config.hmacSecret) {
      console.warn('[PODOrderAPI] hmacSecret missing — getOrderImages will likely 401');
    }

    const ts = Math.floor(Date.now() / 1000).toString();
    const sig = await window.POD_HMAC.signHex(this.config.hmacSecret || '', orderId + ':' + ts);

    const response = await fetch(
      `${this.config.workerEndpoint}/order/images?orderId=${encodeURIComponent(orderId)}`,
      {
        headers: {
          'X-Pod-Signature': sig,
          'X-Pod-Timestamp': ts
        }
      }
    );

    if (!response.ok) {
      let err = {};
      try { err = await response.json(); } catch (e) {}
      throw new Error(err.error || `Fetch failed: ${response.status}`);
    }

    return response.json();
  },

  // ==================== 内部方法 ====================

  /**
   * 调用 Worker API (dashboard 内部端点 - 走 pod-internal-api)
   * 自动加 X-Pod-Signature + X-Pod-Timestamp (DSN-1 修复)
   *
   * Worker 端 verify (pod-internal-api.js:2869): hmac(`${orderId}:${ts}`)
   * - body 必须包含 orderId 字段
   * - 若 body 无 orderId, 此函数会跳过签名 (依赖 worker 的 fallback secret 未配置逻辑)
   *
   * @param {string} endpoint - API 路径 (可带 query string, 如 "/orders/list?limit=10")
   * @param {Object} body - POST 请求体; GET 请求可传 {} (签名仍按 orderId:ts 计算)
   * @param {string} [method='POST'] - HTTP 方法, 默认 POST
   */
  async callWorker(endpoint, body, method = 'POST') {
    /* lazy init: 如果调用方没显式 init() 过, 这里从 window.POD_CONFIG 拉一次配置
     * (dashboard JS 不调 init(), 这里兜底; 避免再报 "hmacSecret not configured"). */
    if (this.config.workerEndpoint === '' && typeof window !== 'undefined' && window.POD_CONFIG) {
      this.init();
    }

    if (!this.isConfigured()) {
      console.warn(`[PODOrderAPI] Worker not configured, using mock for ${endpoint}`);
      return this.mockCall(endpoint, body);
    }

    if (!this.config.hmacSecret) {
      /* 对于 /order/customer-photos，如果 body 有 token 和 sessionId (来自 URL)，使用 URL token 鉴权 */
      const hasUrlAuth = endpoint === '/order/customer-photos' && body && body.token && body.sessionId;
      if (hasUrlAuth) {
        console.log('[PODOrderAPI] Using URL token auth for', endpoint);
        return this.callWorkerUnsigned(endpoint, body);
      }
      console.warn('[PODOrderAPI] hmacSecret not configured and no URL auth — cannot sign request');
      return this.mockCall(endpoint, body);
    }

    try {
      const orderId = (body && body.orderId) || '';
      const ts = Math.floor(Date.now() / 1000).toString();
      const sig = await window.POD_HMAC.signHex(this.config.hmacSecret, orderId + ':' + ts);

      const httpMethod = method.toUpperCase();

      const fetchOpts = {
        method: httpMethod,
        headers: {
          'X-Pod-Signature': sig,
          'X-Pod-Timestamp': ts
        }
      };
      if (httpMethod !== 'GET' && httpMethod !== 'HEAD') {
        fetchOpts.headers['Content-Type'] = 'application/json';
        fetchOpts.body = JSON.stringify(body || {});
      }

      const response = await fetch(`${this.config.workerEndpoint}${endpoint}`, fetchOpts);

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || `API error: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error(`[PODOrderAPI] Error calling ${endpoint}:`, error);
      throw error;
    }
  },

  /**
   * 无签名 fallback (供 /approve /reject /proof 等不需要 HMAC 的端点使用 —
   * 这些端点由页面 token 机制保护, 见 pod-internal-api.js:1806-1820).
   */
  async callWorkerUnsigned(endpoint, body, method = 'POST') {
    if (!this.isConfigured()) {
      return this.mockCall(endpoint, body);
    }

    const response = await fetch(`${this.config.workerEndpoint}${endpoint}`, {
      method: method,
      headers: { 'Content-Type': 'application/json' },
      body: method === 'GET' ? undefined : JSON.stringify(body)
    });

    if (!response.ok) {
      let err = {};
      try { err = await response.json(); } catch (e) {}
      throw new Error(err.error || `API error: ${response.status}`);
    }
    return response.json();
  },

  // ==================== Mock 模式（开发用） ====================

  mockGetSignedUrl(file) {
    return new Promise((resolve) => {
      setTimeout(() => {
        resolve({
          uploadUrl: `https://mock-r2.example.com/proofs/${Date.now()}-${file.name}`,
          publicUrl: `https://mock-cdn.example.com/proofs/${Date.now()}-${file.name}`,
          key: `proofs/${Date.now()}-${file.name}`
        });
      }, 500);
    });
  },

  mockCall(endpoint, body) {
    console.log(`[PODOrderAPI Mock] ${endpoint}:`, body);
    return new Promise((resolve) => {
      setTimeout(() => {
        resolve({
          success: true,
          mock: true,
          endpoint,
          orderId: body.orderId,
          timestamp: new Date().toISOString()
        });
      }, 500);
    });
  }
};

// 自动初始化
if (typeof window !== 'undefined') {
  PODOrderAPI.init();

  // 导出到全局
  window.PODOrderAPI = PODOrderAPI;
}

// 支持 ES Module
if (typeof module !== 'undefined' && module.exports) {
  module.exports = PODOrderAPI;
}
